import React, { useEffect, useMemo, useRef, useState } from 'react';
import CustomDateInput from '../../../components/ui/CustomDateInput';

const PAGE_SIZE = 10;

function HoldingTransactionsTable(props) {
  const loading = Boolean(props?.loading);
  const transactions = useMemo(
    () => (Array.isArray(props?.transactions) ? props.transactions : []),
    [props.transactions] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const onDeleteTransaction =
    typeof props?.onDeleteTransaction === 'function'
      ? props.onDeleteTransaction
      : () => {};
  const onUpdateTransaction =
    typeof props?.onUpdateTransaction === 'function'
      ? props.onUpdateTransaction
      : async () => {};
  const currentPrice = Number(props?.currentPrice || 0);
  const instrumentCurrency = props?.instrumentCurrency || 'USD';
  const title = props?.title || 'Transactions';
  const storageKey = props?.storageKey || null;
  // priceByTicker is used when showing multi-ticker tables (portfolio-level)
  const priceByTicker = props?.priceByTicker || null;

  const [editingId, setEditingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [editError, setEditError] = useState('');

  const [typeFilter, setTypeFilter] = useState('ALL');
  const [notesFilter, setNotesFilter] = useState('');
  const [sortKey, setSortKey] = useState(() => {
    if (!storageKey) return 'date_desc';
    try { return localStorage.getItem(`${storageKey}-sort`) || 'date_desc'; } catch { return 'date_desc'; }
  });
  const [currentPage, setCurrentPage] = useState(1);

  const [form, setForm] = useState({
    quantity: '',
    pricePerUnit: '',
    feeAmount: '',
    tradeCurrency: '',
    counterCurrency: '',
    counterAmount: '',
    occurredAt: '',
    notes: '',
  });

  const transactionTypeOptions = useMemo(() => {
    const types = Array.from(
      new Set(transactions.map((tx) => formatTransactionType(tx.transactionType)))
    ).sort((a, b) => a.localeCompare(b));
    return ['ALL', ...types];
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    const notesQuery = notesFilter.trim().toLowerCase();

    const filtered = transactions.filter((tx) => {
      const typeLabel = formatTransactionType(tx.transactionType);
      const notes = String(tx?.notes || '').toLowerCase();
      if (typeFilter !== 'ALL' && typeLabel !== typeFilter) return false;
      if (notesQuery && !notes.includes(notesQuery)) return false;
      return true;
    });

    filtered.sort((a, b) => {
      switch (sortKey) {
        case 'date_asc':  return new Date(a?.occurredAt||0) - new Date(b?.occurredAt||0);
        case 'date_desc': return new Date(b?.occurredAt||0) - new Date(a?.occurredAt||0);
        case 'price_desc': return Number(b?.pricePerUnit||0) - Number(a?.pricePerUnit||0);
        case 'price_asc':  return Number(a?.pricePerUnit||0) - Number(b?.pricePerUnit||0);
        case 'qty_desc':   return Number(b?.quantity||0) - Number(a?.quantity||0);
        case 'qty_asc':    return Number(a?.quantity||0) - Number(b?.quantity||0);
        case 'gross_desc': return Number(b?.grossAmount||0) - Number(a?.grossAmount||0);
        case 'gross_asc':  return Number(a?.grossAmount||0) - Number(b?.grossAmount||0);
        case 'type_asc':   return formatTransactionType(a.transactionType).localeCompare(formatTransactionType(b.transactionType));
        case 'type_desc':  return formatTransactionType(b.transactionType).localeCompare(formatTransactionType(a.transactionType));
        default: return 0;
      }
    });

    return filtered;
  }, [transactions, typeFilter, notesFilter, sortKey]);

  // Reset to page 1 when filters change
  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedTransactions = filteredTransactions.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const resetFilters = () => {
    setTypeFilter('ALL');
    setNotesFilter('');
    setSortKey('date_desc');
    setCurrentPage(1);
  };

  const editingTransaction = useMemo(
    () =>
      filteredTransactions.find((tx) => tx.id === editingId) ||
      transactions.find((tx) => tx.id === editingId) ||
      null,
    [filteredTransactions, transactions, editingId]
  );

  const startEditing = (tx) => {
    setEditError('');
    setEditingId(tx.id);
    setForm({
      quantity: String(tx.quantity ?? ''),
      pricePerUnit: String(tx.pricePerUnit ?? ''),
      feeAmount: String(tx.feeAmount ?? 0),
      tradeCurrency: tx.tradeCurrency || '',
      counterCurrency: tx.counterCurrency || '',
      counterAmount: tx.counterAmount == null ? '' : String(tx.counterAmount),
      occurredAt: toInputDate(tx.occurredAt),
      notes: tx.notes || '',
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setSavingId(null);
    setEditError('');
    setForm({ quantity: '', pricePerUnit: '', feeAmount: '', tradeCurrency: '', counterCurrency: '', counterAmount: '', occurredAt: '', notes: '' });
  };

  const handleSave = async () => {
    if (!editingTransaction) return;
    try {
      setSavingId(editingTransaction.id);
      setEditError('');
      await onUpdateTransaction(editingTransaction.id, {
        quantity: toNullableNumber(form.quantity),
        pricePerUnit: toNullableNumber(form.pricePerUnit),
        feeAmount: toNullableNumber(form.feeAmount) ?? 0,
        tradeCurrency: form.tradeCurrency || null,
        counterCurrency: form.counterCurrency || null,
        counterAmount: toNullableNumber(form.counterAmount),
        occurredAt: form.occurredAt ? new Date(form.occurredAt + 'T12:00:00').toISOString() : null,
        notes: form.notes,
      });
      cancelEditing();
    } catch (err) {
      setEditError(err.message || 'Failed to update transaction.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <section style={cardStyle}>
      <div style={headerStyle}>
        <h3 style={titleStyle}>{title}</h3>
        <div style={resultsInfoStyle}>
          {filteredTransactions.length} of {transactions.length} · page {safePage}/{totalPages}
        </div>
      </div>

      {editError ? <div style={errorBoxStyle}>{editError}</div> : null}

      {!loading && transactions.length > 0 ? (
        <div style={filtersRowStyle}>
          <CustomSelect
            value={sortKey}
            onChange={(v) => { setSortKey(v); setCurrentPage(1); if (storageKey) { try { localStorage.setItem(`${storageKey}-sort`, v); } catch {} } }}
            options={[
              { value: 'date_desc',  label: 'Newest first' },
              { value: 'date_asc',   label: 'Oldest first' },
              { value: 'price_desc', label: 'Highest price' },
              { value: 'price_asc',  label: 'Lowest price' },
              { value: 'qty_desc',   label: 'Most quantity' },
              { value: 'qty_asc',    label: 'Least quantity' },
              { value: 'gross_desc', label: 'Highest gross' },
              { value: 'gross_asc',  label: 'Lowest gross' },
              { value: 'type_asc',   label: 'Type A–Z' },
              { value: 'type_desc',  label: 'Type Z–A' },
            ]}
          />
          <CustomSelect
            value={typeFilter}
            onChange={(v) => { setTypeFilter(v); setCurrentPage(1); }}
            options={transactionTypeOptions.map((o) => ({ value: o, label: o === 'ALL' ? 'All types' : o }))}
          />
          <input
            type="text"
            value={notesFilter}
            onChange={(e) => { setNotesFilter(e.target.value); setCurrentPage(1); }}
            placeholder="Search notes"
            style={inputStyle}
          />
          <button type="button" onClick={resetFilters} style={resetButtonStyle}>Reset filters</button>
        </div>
      ) : null}

      {loading ? (
        <p style={textStyle}>Loading transactions...</p>
      ) : transactions.length === 0 ? (
        <p style={textStyle}>No {title.toLowerCase()} yet.</p>
      ) : filteredTransactions.length === 0 ? (
        <p style={textStyle}>No transactions match the current filters.</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Ticker</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Quantity</th>
                  <th style={thStyle}>Price</th>
                  <th style={thStyle}>Gross</th>
                  <th style={thStyle}>Fee</th>
                  <th style={thStyle}>Net</th>
                  <th style={thStyle}>Currency</th>
                  <th style={thStyle}>Notes</th>
                  <th style={thStyle}>P/L</th>
                  <th style={thStyle}>P/L %</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedTransactions.map((tx) => {
                  const isEditing = tx.id === editingId;
                  const isSaving = tx.id === savingId;
                  const ticker = String(tx?.instrument?.ticker || '').toUpperCase();

                  return (
                    <React.Fragment key={tx.id}>
                      <tr>
                        <td style={tdStyle}>
                          <span style={{ color: tx.transactionType === 0 ? '#22c55e' : tx.transactionType === 1 ? '#ef4444' : 'var(--text-primary)', fontWeight: 700 }}>
                            {formatTransactionType(tx.transactionType)}
                          </span>
                        </td>
                        <td style={tdStyle}>{ticker || '-'}</td>
                        <td style={tdStyle}>{formatDate(tx.occurredAt)}</td>
                        <td style={tdStyle}>{Number(tx.quantity || 0)}</td>
                        <td style={tdStyle}>{formatCurrency(tx.pricePerUnit, tx.tradeCurrency)}</td>
                        <td style={tdStyle}>{formatCurrency(tx.grossAmount, tx.tradeCurrency)}</td>
                        <td style={tdStyle}>{formatCurrency(tx.feeAmount, tx.tradeCurrency)}</td>
                        <td style={tdStyle}>{formatCurrency(tx.netAmount, tx.tradeCurrency)}</td>
                        <td style={tdStyle}>
                          {tx.tradeCurrency || '-'}
                          {tx.counterCurrency ? ` → ${tx.counterCurrency}` : ''}
                        </td>
                        <td style={tdStyle}>{tx.notes || '-'}</td>
                        {(() => {
                          const isBuyOrSell = tx.transactionType === 0 || tx.transactionType === 1;
                          const txTicker = String(tx?.instrument?.ticker || '').toUpperCase();
                          const resolvedPrice = priceByTicker
                            ? (priceByTicker[txTicker]?.currentPrice || 0)
                            : currentPrice;
                          const resolvedCurrency = priceByTicker
                            ? (priceByTicker[txTicker]?.currency || instrumentCurrency)
                            : instrumentCurrency;
                          const price = Number(tx.pricePerUnit || 0);
                          if (isBuyOrSell && resolvedPrice > 0 && price > 0) {
                            const pl = (resolvedPrice - price) * Number(tx.quantity || 0);
                            const plPct = ((resolvedPrice - price) / price) * 100;
                            const color = pl >= 0 ? '#22c55e' : '#ef4444';
                            return (
                              <>
                                <td style={tdStyle}>
                                  <span style={{ color, fontWeight: 700 }}>
                                    {pl >= 0 ? '+' : ''}{formatCurrency(pl, resolvedCurrency)}
                                  </span>
                                </td>
                                <td style={tdStyle}>
                                  <span style={{ color, fontWeight: 700 }}>
                                    {plPct >= 0 ? '+' : ''}{plPct.toFixed(2)}%
                                  </span>
                                </td>
                              </>
                            );
                          }
                          return <><td style={tdStyle}>-</td><td style={tdStyle}>-</td></>;
                        })()}
                        <td style={tdStyle}>
                          <div style={actionsRowStyle}>
                            <button type="button" onClick={() => startEditing(tx)} style={editButtonStyle} title="Edit transaction" aria-label="Edit transaction">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                            </button>
                            <button type="button" onClick={() => onDeleteTransaction(tx.id)} style={deleteButtonStyle} title="Delete transaction" aria-label="Delete transaction">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isEditing ? (
                        <tr>
                          <td colSpan={13} style={editRowCellStyle}>
                            <div style={editPanelStyle}>
                              <div style={editGridStyle}>
                                <label style={fieldStyle}>
                                  <span style={labelStyle}>Quantity</span>
                                  <input type="number" step="any" value={form.quantity} onChange={(e) => setForm((prev) => ({ ...prev, quantity: e.target.value }))} style={inputStyle} />
                                </label>
                                <label style={fieldStyle}>
                                  <span style={labelStyle}>Price per unit</span>
                                  <input type="number" step="any" value={form.pricePerUnit} onChange={(e) => setForm((prev) => ({ ...prev, pricePerUnit: e.target.value }))} style={inputStyle} />
                                </label>
                                <label style={fieldStyle}>
                                  <span style={labelStyle}>Fee</span>
                                  <input type="number" step="any" value={form.feeAmount} onChange={(e) => setForm((prev) => ({ ...prev, feeAmount: e.target.value }))} style={inputStyle} />
                                </label>
                                <label style={fieldStyle}>
                                  <span style={labelStyle}>Trade currency</span>
                                  <input type="text" value={form.tradeCurrency} onChange={(e) => setForm((prev) => ({ ...prev, tradeCurrency: e.target.value.toUpperCase() }))} style={inputStyle} />
                                </label>
                                {tx.transactionType === 8 ? (
                                  <>
                                    <label style={fieldStyle}>
                                      <span style={labelStyle}>Counter currency</span>
                                      <input type="text" value={form.counterCurrency} onChange={(e) => setForm((prev) => ({ ...prev, counterCurrency: e.target.value.toUpperCase() }))} style={inputStyle} />
                                    </label>
                                    <label style={fieldStyle}>
                                      <span style={labelStyle}>Counter amount</span>
                                      <input type="number" step="any" value={form.counterAmount} onChange={(e) => setForm((prev) => ({ ...prev, counterAmount: e.target.value }))} style={inputStyle} />
                                    </label>
                                  </>
                                ) : null}
                                <label style={fieldStyle}>
                                  <span style={labelStyle}>Date</span>
                                  <CustomDateInput
                                    value={form.occurredAt}
                                    onChange={(v) => setForm((prev) => ({ ...prev, occurredAt: v }))}
                                    placeholder="Select date"
                                  />
                                </label>
                                <label style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
                                  <span style={labelStyle}>Notes</span>
                                  <input type="text" value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} style={inputStyle} />
                                </label>
                              </div>
                              <div style={editActionsStyle}>
                                <button type="button" onClick={handleSave} disabled={isSaving} style={saveButtonStyle}>{isSaving ? 'Saving...' : 'Save'}</button>
                                <button type="button" onClick={cancelEditing} disabled={isSaving} style={cancelButtonStyle}>Cancel</button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={paginationStyle}>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                style={{ ...pageButtonStyle, opacity: safePage === 1 ? 0.4 : 1 }}
                aria-label="Previous page"
              >
                ‹
              </button>
              <span style={pageInfoStyle}>
                {safePage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                style={{ ...pageButtonStyle, opacity: safePage === totalPages ? 0.4 : 1 }}
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function toNullableNumber(value) {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toInputDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function formatTransactionType(type) {
  switch (type) {
    case 0: return 'BUY';
    case 1: return 'SELL';
    case 2: return 'DIVIDEND';
    case 3: return 'DEPOSIT';
    case 4: return 'WITHDRAWAL';
    case 5: return 'FEE';
    case 6: return 'INTEREST';
    case 7: return 'TAX';
    case 8: return 'FX';
    default: return String(type ?? '-');
  }
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

function formatCurrency(value, currency) {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 2 }).format(Number(value || 0));
  } catch {
    return Number(value || 0).toFixed(2) + ' ' + (currency || '');
  }
}

// ── Custom Select ─────────────────────────────────────────────────────────────
function CustomSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selected = options.find((o) => o.value === value);
  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={selectTriggerStyle}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected?.label ?? value}</span>
        <span style={{ flexShrink: 0, fontSize: '11px', opacity: 0.6 }}>▾</span>
      </button>
      {open && (
        <div style={selectDropdownStyle}>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{ ...selectOptionStyle, ...(opt.value === value ? selectOptionActiveStyle : {}) }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const selectTriggerStyle = { width: '100%', minHeight: '42px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, textAlign: 'left' };
const selectDropdownStyle = { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--surface-strong)', border: '1px solid var(--border)', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 200, padding: '6px', display: 'grid', gap: '2px', maxHeight: '260px', overflowY: 'auto' };
const selectOptionStyle = { width: '100%', padding: '9px 12px', borderRadius: '8px', border: 'none', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '14px', fontWeight: 600, textAlign: 'left' };
const selectOptionActiveStyle = { background: 'var(--primary)', color: '#fff' };

const cardStyle = { background: 'var(--surface-strong)', borderRadius: '18px', padding: '24px', marginBottom: '24px', boxShadow: 'var(--shadow)', border: '1px solid var(--border)', minWidth: 0, width: '100%', boxSizing: 'border-box' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' };
const titleStyle = { margin: 0, color: 'var(--text-primary)' };
const resultsInfoStyle = { color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 600 };
const textStyle = { color: 'var(--text-primary)' };
const errorBoxStyle = { marginBottom: '16px', padding: '12px 14px', borderRadius: '12px', background: 'rgba(220, 38, 38, 0.12)', color: '#fca5a5', border: '1px solid rgba(220, 38, 38, 0.35)' };
const filtersRowStyle = { marginBottom: '18px', padding: '10px 14px', borderRadius: '14px', background: 'var(--surface)', border: '1px solid var(--border)', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(160px, 100%), 1fr))', gap: '10px' };
const resetButtonStyle = { border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 14px', background: 'var(--surface-strong)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap', width: '100%' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', color: 'var(--text-primary)' };
const thStyle = { textAlign: 'left', padding: '12px', borderBottom: '1px solid var(--table-border)' };
const tdStyle = { padding: '12px', borderBottom: '1px solid var(--table-row-border)', verticalAlign: 'top' };
const editRowCellStyle = { padding: '16px', borderBottom: '1px solid var(--table-row-border)', background: 'rgba(255,255,255,0.02)' };
const editPanelStyle = { display: 'grid', gap: '16px' };
const editGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: '12px' };
const fieldStyle = { display: 'grid', gap: '6px' };
const labelStyle = { fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' };
const inputStyle = { width: '100%', boxSizing: 'border-box', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', padding: '10px 12px', outline: 'none' };
const actionsRowStyle = { display: 'flex', gap: '8px', alignItems: 'center' };
const editActionsStyle = { display: 'flex', gap: '10px', justifyContent: 'flex-end' };
const editButtonStyle = { width: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: '50%', background: 'var(--primary)', color: '#fff', cursor: 'pointer', padding: 0 };
const saveButtonStyle = { border: 'none', borderRadius: '10px', padding: '10px 14px', background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontWeight: 700 };
const cancelButtonStyle = { border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 14px', background: 'var(--surface)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 700 };
const deleteButtonStyle = { width: '32px', height: '32px', borderRadius: '50%', border: 'none', background: 'var(--danger)', color: '#fff', cursor: 'pointer', fontWeight: 800 };
const paginationStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '16px' };
const pageButtonStyle = { border: '1px solid var(--border)', borderRadius: '10px', padding: '8px 16px', background: 'var(--surface)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 700, fontSize: '18px', lineHeight: 1 };
const pageInfoStyle = { color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 600, minWidth: '60px', textAlign: 'center' };

export default HoldingTransactionsTable;
